import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const KakaoCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // 이미 처리했으면 스킵 (React Strict Mode 중복 실행 방지)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    handleKakaoCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKakaoCallback = async () => {
    try {
      // URL에서 인증 코드 추출
      const params = new URLSearchParams(location.search);
      const code = params.get('code');

      if (!code) {
        console.error('[KakaoCallback] Authorization code 없음');
        alert('인증에 실패했습니다.');
        navigate('/login', { replace: true });
        return;
      }

      console.log('[KakaoCallback] 카카오 인증 코드 받음:', code);

      // 동적 BASE_URL 설정
      const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
      const redirectUri = `${baseUrl}/auth/callback/kakao`;

      console.log('[KakaoCallback] Redirect URI:', redirectUri);

      // REST API로 토큰 교환 (SDK 불필요)
      const tokenResponse = await fetch('https://kauth.kakao.com/oauth/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: process.env.REACT_APP_KAKAO_REST_API_KEY!,
          redirect_uri: redirectUri,
          code: code,
        }),
      });

      const tokenData = await tokenResponse.json();

      if (tokenData.error) {
        console.error('토큰 교환 실패:', tokenData);
        alert('로그인에 실패했습니다.');
        navigate('/login', { replace: true });
        return;
      }

      // 사용자 정보 가져오기
      const userResponse = await fetch('https://kapi.kakao.com/v2/user/me', {
        headers: {
          Authorization: `Bearer ${tokenData.access_token}`,
          'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
        },
      });

      const userData = await userResponse.json();

      if (userData.id) {
        const snsId = `kakao_${userData.id}`;
        const email = userData.kakao_account?.email || '';
        const name = userData.kakao_account?.profile?.nickname || '';

        console.log('[KakaoCallback] 카카오 사용자 정보:', userData);
        console.log('[KakaoCallback] SNS ID:', snsId);

        // 1. 먼저 Supabase Auth 세션 생성
        console.log('[KakaoCallback] Supabase Auth 세션 생성 중...');
        await login(snsId);
        console.log('[KakaoCallback] Supabase Auth 세션 생성 완료');

        // 2. 이제 인증된 상태에서 DB 조회 (대소문자 구분 없이)
        console.log('[KakaoCallback] DB 조회 시작...');
        const { data: existingCustomer, error } = await supabase
          .from('customers')
          .select('*')
          .ilike('sns_id', snsId)
          .maybeSingle();

        console.log('[KakaoCallback] DB 조회 완료:', existingCustomer ? '회원 존재' : '회원 없음', error);

        if (existingCustomer) {
          // 기존 회원 - 메인으로 이동 (replace로 callback 히스토리 제거)
          console.log('[KakaoCallback] 기존 회원:', existingCustomer.nickname);
          navigate('/main', { replace: true });
        } else {
          // 신규 회원 - 회원가입 플로우로 이동 (replace로 callback 히스토리 제거)
          console.log('[KakaoCallback] 신규 회원 - 회원가입 진행');
          navigate('/signup/phone-verification', {
            replace: true,
            state: {
              snsType: 'kakao',
              snsId,
              email,
              name,
            },
          });
        }
      } else {
        console.error('사용자 정보 가져오기 실패:', userData);
        alert('사용자 정보를 가져오는데 실패했습니다.');
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('카카오 로그인 처리 오류:', error);
      alert('로그인 중 오류가 발생했습니다.');
      navigate('/login', { replace: true });
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#1e1e1e',
        color: '#ffffff',
        fontFamily: 'SUIT, sans-serif',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <h2>카카오 로그인 처리 중...</h2>
        <p>잠시만 기다려주세요.</p>
      </div>
    </div>
  );
};

export default KakaoCallback;
